import React, { FC } from 'react';
import styled from '@emotion/styled';
import { useQuery, gql } from '@apollo/client';

import 'react-calendar/dist/Calendar.css';

import { useSelector, useI18n, useStaffCan, useButton } from '~Hooks';

import { useReportsApi } from '~Components/ReportsUniverse';
import { useDispatch } from '~Hooks';
import { useCashDrawer } from '~Providers/CashDrawerProvider';
import HistoryIcon from '~Assets/icons/history.svg';
import * as actions from '~actions';

import Icon from '~ComponentsImproved/Icon';
import PayoutHistory from './Components/PayoutHistory';
import LoginModal from '~Scenes/Tables/Components/LoginModal';
import { GetVenuesCash, GetVenuesCashVariables } from './__queries__';
import BackTopBar from '~ComponentsImproved/BackTopBar';
import { BlueButton, Box, TransparentButton } from '~Styles';
import Modal from '~ComponentsImproved/Modal';
import useModal from '~ComponentsImproved/Modal/useModal';
import CashRegisterBreakdown from './Components/CashRegisterBreakdown';
import CashSourceManager from './Components/CashSourceManager';

const CASH_QUERY = gql`
  query GetVenuesCash($venueId: ID!) {
    venue: Venue(id: $venueId) {
      id
      cashFloat
      cashSources {
        id
        name
      }
    }
  }
`;

const Wrapper = styled(Box)`
  flex: 1;
  background-color: var(--colorBlueGray1200);
  color: var(--colorWhite);
  height: calc(100vh - 64px);
  position: fixed;
  width: 100%;
  height: 100%;
`;

const ScrollWrapper = styled(Box)`
  overflow-y: auto;
  height: 100%;
  flex: 1;
  padding: 20px 40px 0px 20px;
  box-sizing: border-box;
  opacity: 0;
  animation: fadeIn 0.2s ease-in-out 0.1s forwards;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const OpenCashDrawerButton = styled(BlueButton)`
  height: 24px;
`;

const OpenHistoryButton = styled(TransparentButton)`
  height: 24px;
`;

const CashSources: FC = () => {
  const { i18n } = useI18n();
  const [, , venueId] = location.hash.split('/');
  const staffCan = useStaffCan();
  const reportsApi = useReportsApi();
  const cashDrawer = useCashDrawer();

  const staffs = useSelector((state) => state.companyStaff?.staffs ?? []);
  const staffId = useSelector((state) => state.staff?.id);
  const hasStaff = staffs.length > 0;

  const historyModal = useModal();

  const dispatch = useDispatch();

  const language = useSelector((state) => state.language);

  let lang: string = language;
  if (language == 'se') lang = 'sv';

  const cashQuery = useQuery<GetVenuesCash, GetVenuesCashVariables>(CASH_QUERY, {
    fetchPolicy: 'network-only',
    variables: {
      venueId,
    },
  });

  const openHistoryButton = useButton({
    onClick: () => historyModal.show(),
    disabled: hasStaff && !staffId,
  });

  const openCashDrawerButton = useButton({
    onClick: () => {
      dispatch(actions.addToast(i18n('Cash.DrawerOpened'), true));
      reportsApi.cashBoxOpened();
      cashDrawer.open();
    },
    disabled: !staffCan.CLOSE_DAY,
    disabledToast: i18n('Cash.DrawerOpen.PermissionDenied'),
  });

  return (
    <>
      <Wrapper display="flex" flexDirection="column">
        <BackTopBar title={i18n('Menu.CashManagement')} dark>
          <Box display="flex" alignItems="center" gap="xs">
            <OpenCashDrawerButton size="small" {...openCashDrawerButton}>
              {i18n('CashManagement.OpenCashDrawer')}
            </OpenCashDrawerButton>
            <OpenHistoryButton
              size="small"
              display="flex"
              alignItems="center"
              justifyContent="center"
              {...openHistoryButton}>
              <Icon src={HistoryIcon} color="var(--colorBlueGray200)" width="24px" height="24px" />
            </OpenHistoryButton>
          </Box>
        </BackTopBar>

        <ScrollWrapper display="flex" flexDirection="column" alignItems="center" gap="large">
          {staffCan.CLOSE_DAY && !cashQuery.loading && (
            <Box fluid display="flex" justifyContent="center" gap="xl" marginTop="medium">
              <CashRegisterBreakdown cashFloat={cashQuery.data?.venue?.cashFloat || 0} />
              <CashSourceManager cashSources={cashQuery.data?.venue?.cashSources || []} />
            </Box>
          )}
        </ScrollWrapper>
      </Wrapper>

      {!staffId && <LoginModal />}

      <Modal isShowing={historyModal.isShowing} hide={historyModal.hide}>
        <PayoutHistory cashSources={cashQuery.data?.venue?.cashSources || []} />
      </Modal>
    </>
  );
};

export default CashSources;
